import moment from "moment";

import { MetricPeriodType, PortfolioNamesDocument } from "~/graphql/generated/graphql";

import { type BenchmarkGroupOption, type OpexRowItem, type OpexSettingsTemplate, type PropertyOpexColumn } from "~/helpers/modules/opex-helpers";
import type { BalanceDateRange, ValueHeaderKey } from "~/helpers/modules/accounts-helpers";
import { type TenanciesColumnKey, type TenanciesDate } from "~/helpers/entities/tenancy-helpers";
import { ApolloClient } from "@apollo/client/core";
import { cloneDeep } from "~/helpers/common-helpers";
import type { NamespacePortfolioPropertyBalance_0, NamespacePortfolioPropertyBalance_1 } from "~/helpers/user-preferences-old-versions";

/** Note Sven: Deprecated, but still used as a type for migration */
export type NamespacePortfolioPropertyTenanciesColumnSettings = {
  [k in TenanciesColumnKey]: {
    enabled: boolean;
    position: number;
  };
};

// export type NamespacePortfolioPropertyTenancies = {
//   selectedDate: TenanciesDate;
//   columns: TenanciesColumnKey[];
//   pageSize: number;
// };

export enum NumberDisplay {
  Absolute = "ABSOLUTE",
  Thousands = "THOUSANDS",
  Millions = "MILLIONS",
}

export type NamespaceChrome = {
  splitPercentage: number;
  containerFocusIndex: number;
};

export interface DateRangeConfig {
  periodType: MetricPeriodType;
  dateRange: BalanceDateRange;
  customDateStart: string | undefined;
  customDateEnd: string | undefined;
  referenceDate?: string;
}

export type PeriodRangeType = "yearWhole" | "yearRolling" | "yearToDate" | "quarterWhole" | "quarterRolling";

export type BudgetsConfig = Record<string, string[]>;

export interface PreferencesNamespaceBase {
  type: PreferencesNamespaceType;
}

export interface NamespaceLocale extends PreferencesNamespaceBase {
  type: "preferenceNamespaceLocale";
  locale: string;
}

export interface NamespacePortfolioPortfolio extends PreferencesNamespaceBase {
  type: "preferenceNamespacePortfolioPortfolio";

  churnFilterDateTo: string;
  churnFilterDateFrom: string;
  churnFilterPeriodType: MetricPeriodType;

  idleTenancyPercentFilterDateTo: string;
  idleTenancyPercentFilterDateFrom: string;
  idleTenancyPercentFilterPeriodType: MetricPeriodType;

  rentFilterDateTo: string;
  rentFilterDateFrom: string;
  rentFilterPeriodType: MetricPeriodType;
}

export interface NamespacePortfolioPropertyOverview extends PreferencesNamespaceBase {
  type: "preferenceNamespacePortfolioPropertyOverview";
  idleTenancyRateFilterDateTo: string;
  idleTenancyRateFilterDateFrom: string;
  idleTenancyRateFilterPeriodType: MetricPeriodType;

  evictionRateFilterDateTo: string;
  evictionRateFilterDateFrom: string;
  evictionRateFilterPeriodType: MetricPeriodType;

  rentRateFilterDateTo: string;
  rentRateFilterDateFrom: string;
  rentRateFilterPeriodType: MetricPeriodType;
}

export interface NamespaceOpex extends PreferencesNamespaceBase {
  type: "preferencesNamespaceOpex";
  template: string;
  templateModified: boolean;
  savedTemplates: OpexSettingsTemplate[];
  includeZeroRows: boolean;
  benchmark: BenchmarkGroupOption;
  periodRangeType: PeriodRangeType;
  periodOffset: number;
  normaliseBy: "tenancies" | "area" | "none";
  excludePropertyIds: ("properties" | "companies" | string)[];
  referenceMonthOffset: number | undefined;
  referencePercentage: boolean;
  annualized: boolean;
  includePortfolioIds?: string[];
  includeCategories?: DataItem[];
}

export interface NamespacePropertyOpex extends PreferencesNamespaceBase {
  type: "preferencesNamespacePropertyOpex";
  includeZeroRows: boolean;
  periodRangeType: PeriodRangeType;
  periodOffset: number;
  annualized: boolean;
  includeCategories?: OpexRowItem[];
  showColumns: PropertyOpexColumn[];
  showPerAreaColumns: PropertyOpexColumn[];
  propertyBudgets: Record<string, string[]>;
}

export interface NamespacePortfolioPropertyBalance extends PreferencesNamespaceBase, DateRangeConfig {
  type: "preferencesNamespacePortfolioPropertyBalance_2";
  portfolioId: Nullable<string>;
  propertyId: Nullable<string>;
  numberDisplay: NumberDisplay;
  includeZeroAccounts: boolean;
  includeZeroCategories: boolean;
  useChangeColors: boolean;
  showGrandTotal: boolean;
  columns: ValueHeaderKey[];
  propertyBudgets: BudgetsConfig;
  groupColumnType: "period" | "property";
}

export interface NamespacePortfolioPropertyTenancies extends PreferencesNamespaceBase {
  type: "preferencesNamespacePortfolioPropertyTenancies";

  selectedDate: TenanciesDate;
  pageSize: number;
}

export interface PreferencesNamespaceFrontpage extends PreferencesNamespaceBase {
  type: "preferencesNamespaceFrontpage";

  widgets: Widget[];
}

export interface PreferencesNamespaceAssets extends PreferencesNamespaceBase {
  type: "preferencesNamespaceAssets";

  urlParamsProperties: Record<string, string | undefined> | undefined;
}

type PreferencesNamespace =
  | NamespaceLocale
  | NamespacePortfolioPortfolio
  | NamespacePortfolioPropertyOverview
  | NamespacePortfolioPropertyTenancies
  | NamespacePortfolioPropertyBalance
  | NamespacePropertyOpex
  | NamespaceOpex
  | PreferencesNamespaceFrontpage
  | PreferencesNamespaceAssets;

type PreferencesNamespaceType = PreferencesNamespace["type"];

export type UserPreferences = {
  preferenceNamespaceLocale: NamespaceLocale;

  preferencesNamespacePropertyOpex: NamespacePropertyOpex;

  preferencesNamespaceOpex: NamespaceOpex;

  preferencesNamespacePortfolioOpex: NamespaceOpex;

  preferencesNamespacePortfolioPropertyBalance: NamespacePortfolioPropertyBalance;

  preferenceNamespacePortfolioPortfolio: NamespacePortfolioPortfolio;

  preferenceNamespacePortfolioPropertyOverview: NamespacePortfolioPropertyOverview;

  preferenceNamespacePortfolioPropertyTenancies: NamespacePortfolioPropertyTenancies;

  frontpage: PreferencesNamespaceFrontpage;

  assets: PreferencesNamespaceAssets;
};

export type NamespaceName = keyof UserPreferences;
export type Namespace = PreferencesNamespace;

// Matches valid Roman numerals (I–MMMM, i.e. 1–4999)
const ROMAN_RE = /^M{0,4}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$/i;

const romanToInt = (s: string): number | null => {
  if (!s || !ROMAN_RE.test(s)) return null;
  const vals: Record<string, number> = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000 };
  let n = 0;
  const u = s.toUpperCase();
  for (let i = 0; i < u.length; i++) {
    const cur = vals[u[i]], nxt = vals[u[i + 1]];
    if (nxt && cur < nxt) { n += nxt - cur; i++; } else n += cur;
  }
  return n || null;
};

/**
 * Sorts names alphabetically, but treats a trailing Roman numeral suffix as a
 * number so that e.g. "Fund IX" (9) sorts after "Fund V" (5).
 * Falls back to plain localeCompare when the prefix differs or no Roman
 * numeral is detected.
 */
const comparePortfolioNames = (a: string, b: string): number => {
  const split = (name: string): [string, number | null] => {
    const m = name.match(/^(.*?)\s+([IVXLCDM]+)$/i);
    return m ? [m[1], romanToInt(m[2])] : [name, null];
  };
  const [pa, na] = split(a);
  const [pb, nb] = split(b);
  if (na !== null && nb !== null && pa.localeCompare(pb) === 0) return na - nb;
  return a.localeCompare(b);
};

export const DEFAULTS: {
  [k in NamespaceName]: UserPreferences[k] | ((preferences: UserPreferences[k] | undefined, client: ApolloClient<any>) => Promise<UserPreferences[k]>);
} = {
  preferenceNamespaceLocale: {
    type: "preferenceNamespaceLocale",

    locale: "da-dk",
  },

  preferencesNamespacePropertyOpex: {
    type: "preferencesNamespacePropertyOpex",

    propertyBudgets: {},
    includeZeroRows: false,
    periodOffset: 0,
    periodRangeType: "yearToDate",
    annualized: false,
    showColumns: [],
    showPerAreaColumns: [],
    includeCategories: undefined,
  },

  preferencesNamespaceOpex: {
    type: "preferencesNamespaceOpex",

    savedTemplates: [],
    template: "normLatest12",
    templateModified: false,
    includeZeroRows: false,
    benchmark: "average.total",
    periodOffset: 0,
    periodRangeType: "yearWhole",
    normaliseBy: "area",
    excludePropertyIds: [],
    includePortfolioIds: undefined,
    includeCategories: undefined,
    referenceMonthOffset: undefined,
    referencePercentage: false,
    annualized: false,
  },

  preferencesNamespacePortfolioOpex: {
    type: "preferencesNamespaceOpex",

    savedTemplates: [],
    template: "normLatest12",
    templateModified: false,
    includeZeroRows: false,
    benchmark: "average.total",
    periodOffset: 0,
    periodRangeType: "yearWhole",
    normaliseBy: "area",
    excludePropertyIds: [],
    includeCategories: undefined,
    referenceMonthOffset: undefined,
    referencePercentage: false,
    annualized: false,
  },

  preferencesNamespacePortfolioPropertyBalance: {
    type: "preferencesNamespacePortfolioPropertyBalance_2",

    propertyId: undefined,
    portfolioId: undefined,
    dateRange: "latest8",
    customDateEnd: undefined,
    customDateStart: undefined,
    periodType: MetricPeriodType.Quarterly,
    numberDisplay: NumberDisplay.Absolute,
    includeZeroAccounts: false,
    includeZeroCategories: true,
    useChangeColors: false,
    showGrandTotal: false,
    columns: ["total"],
    propertyBudgets: {},
    groupColumnType: "period",
  },

  preferenceNamespacePortfolioPortfolio: {
    type: "preferenceNamespacePortfolioPortfolio",

    churnFilterDateTo: moment().utc().format("YYYY-MM-DD"),
    churnFilterDateFrom: moment().subtract(5, "year").utc().format("YYYY-MM-DD"),
    churnFilterPeriodType: MetricPeriodType.Quarterly,

    idleTenancyPercentFilterDateTo: moment().utc().format("YYYY-MM-DD"),
    idleTenancyPercentFilterDateFrom: moment().subtract(5, "year").utc().format("YYYY-MM-DD"),
    idleTenancyPercentFilterPeriodType: MetricPeriodType.Quarterly,

    rentFilterDateTo: moment().utc().format("YYYY-MM-DD"),
    rentFilterDateFrom: moment().subtract(5, "year").utc().format("YYYY-MM-DD"),
    rentFilterPeriodType: MetricPeriodType.Quarterly,
  },

  preferenceNamespacePortfolioPropertyOverview: {
    type: "preferenceNamespacePortfolioPropertyOverview",

    idleTenancyRateFilterDateTo: moment().utc().format("YYYY-MM-DD"),
    idleTenancyRateFilterDateFrom: moment().subtract(5, "year").utc().format("YYYY-MM-DD"),
    idleTenancyRateFilterPeriodType: MetricPeriodType.Quarterly,

    evictionRateFilterDateTo: moment().utc().format("YYYY-MM-DD"),
    evictionRateFilterDateFrom: moment().subtract(5, "year").utc().format("YYYY-MM-DD"),
    evictionRateFilterPeriodType: MetricPeriodType.Quarterly,

    rentRateFilterDateTo: moment().utc().format("YYYY-MM-DD"),
    rentRateFilterDateFrom: moment().subtract(5, "year").utc().format("YYYY-MM-DD"),
    rentRateFilterPeriodType: MetricPeriodType.Quarterly,
  },

  preferenceNamespacePortfolioPropertyTenancies: async (preferences) => {
    const columnsFromOldSettings = migrateOldTenanciesColumnSettings(preferences);

    return {
      type: "preferencesNamespacePortfolioPropertyTenancies",

      pageSize: preferences?.pageSize ?? 25,
      selectedDate: preferences?.selectedDate ?? "today",
    };
  },

  frontpage: async (preferences, client) => {
    const getWidgets = async (): Promise<Widget[]> => {
      const portfolios = await client.query({
        query: PortfolioNamesDocument,
        variables: {
          filters: { groups: [{ matches: [{ active: true }] }] },
        },
      });

      return [
        widgetCountDefault("portfolio"),
        widgetCountDefault("property"),
        widgetCountDefault("tenancy"),
        ...[...portfolios.data.assetManagementPortfolios.items]
          .sort((a, b) => (a.name && b.name ? comparePortfolioNames(a.name, b.name) : 0))
          .map((pf) => widgetPortfolioDefault(pf.id)),
      ];
    };

    return {
      type: "preferencesNamespaceFrontpage",
      widgets: preferences?.widgets ?? (await getWidgets()),
    };
  },

  assets: {
    type: "preferencesNamespaceAssets",

    urlParamsProperties: undefined,
  },
};

const migrateOldTenanciesColumnSettings = (preferences?: Object) => {
  if (!preferences || "includeColumns" in preferences === false) return;

  const oldColumns = preferences as { includeColumns: { global: NamespacePortfolioPropertyTenanciesColumnSettings } };

  return Object.entries(oldColumns.includeColumns.global)
    .filter(([, value]) => value.enabled)
    .sort(([, a], [, b]) => a.position - b.position)
    .map(([key]) => key as TenanciesColumnKey);
};

// Add migrations here by old type name
const migrations = {
  preferencesNamespacePortfolioPropertyBalance: (oldPreferences: NamespacePortfolioPropertyBalance_0) => {
    const propertyBudgets: BudgetsConfig = {};

    for (const propertyId in oldPreferences.propertyBudgets) {
      propertyBudgets[propertyId] = oldPreferences.propertyBudgets[propertyId].map((budget) => budget.id);
    }

    return <NamespacePortfolioPropertyBalance_1>{
      ...oldPreferences,
      type: "preferencesNamespacePortfolioPropertyBalance_1",
      propertyBudgets,
    };
  },
  preferencesNamespacePortfolioPropertyBalance_1: (oldPreferences: NamespacePortfolioPropertyBalance_1) => {
    const columns: ValueHeaderKey[] = [];

    if (oldPreferences.showColumns.totalCostWithVat) columns.push("total");
    if (oldPreferences.showColumns.change) columns.push("change");
    if (oldPreferences.showColumns.changePercentage) columns.push("changePercentage");
    if (oldPreferences.showBudgetForecasts) columns.push("budgetForecast");
    if (oldPreferences.showBudgetsTotal) columns.push("budget");
    if (oldPreferences.showBudgetDiff) columns.push("budgetDiff");

    return <NamespacePortfolioPropertyBalance>{
      ...oldPreferences,
      type: "preferencesNamespacePortfolioPropertyBalance_2",
      columns,
      showGrandTotal: false,
    };
  },
} satisfies { [k in string]: (oldPreferences: any) => { type: string } };

const migratePreferences = <O extends { type: string }, T extends PreferencesNamespace>(preferences: O, targetNamespaceType: T) => {
  let newPreferences: any = preferences;

  while (newPreferences.type !== targetNamespaceType.type) {
    const _type = newPreferences.type as keyof typeof migrations;

    if (!_type) newPreferences = targetNamespaceType;
    else if (_type in migrations) newPreferences = migrations[_type](newPreferences);
    else throw new Error(`Unknown user preferences migration type: ${_type}`);
  }

  return newPreferences as T;
};

const setNamespaceDefaults = <T extends Namespace>(defaultPreferencesNamespaced: T, savedPreferencesNamespaced: T): T => {
  const output = {} as typeof defaultPreferencesNamespaced;

  let key: keyof T;

  for (key in defaultPreferencesNamespaced)
    output[key] = savedPreferencesNamespaced[key] === undefined ? defaultPreferencesNamespaced[key] : savedPreferencesNamespaced[key];

  return output;
};

export const getNamespace = async <T extends NamespaceName>(
  namespace: T,
  preferences: Partial<UserPreferences>,
  client: ApolloClient<any>
): Promise<UserPreferences[T]> => {
  const defaultPreferences = DEFAULTS[namespace];

  const resolvedDefaultPreferences = (
    typeof defaultPreferences === "function" ? await defaultPreferences(preferences[namespace], client) : cloneDeep(defaultPreferences)
  ) as UserPreferences[T];

  if (preferences[namespace] === undefined) {
    return resolvedDefaultPreferences;
  } else {
    try {
      const migratedPreferences = migratePreferences(preferences[namespace], resolvedDefaultPreferences);

      return setNamespaceDefaults<UserPreferences[T]>(resolvedDefaultPreferences, migratedPreferences);
    } catch (error) {
      console.warn(error);

      return resolvedDefaultPreferences;
    }
  }
};
